import { put } from 'redux-saga/effects';

import RequestService from 'common/services/api/RequestService';
import { apiRoots } from 'common/services/api/apiConstants';
import { Alert, AlertLevel } from 'common/components/notifications/types';
import { pushAlert } from 'common/components/notifications/actions';

import { ApiErrorHandler, ItemBankInfo } from '../store/context/types';
import { DataSourceResponse } from '../features/crossTestMetrics/types';
import { DynamicAnswerKeyEvents, EditQuestionInContextReq } from '../store/context/reducer';
import { UpdateCheckStatus, UpdateContentInContextToLiveRevisionData } from '../types';
import { AddApplicationsPayload, AddHashesPayload, ConfigInfo } from '../features/softwareBlocklistOverlay/types';
import { AddDeviceIdsPayload } from '../features/deviceIdsBlocklistOverlay/types';
import {
    ADD_SOFTWARE, ADD_SOFTWARE_WITH_HASHES, REMOVE_SOFTWARE, ADD_HASHES, REMOVE_HASHES,
} from '../features/softwareBlocklistOverlay/constants';

let requestService: RequestService;

export const configureApiService = (reqService: RequestService): void => {
    requestService = reqService;
};

const apiService = {
    async fetchAuthorFilters(filterType: string, itemBankId: number, workspaceRef: string, signal?: AbortSignal) {
        const url = `${apiRoots.ASSESSMENT}GetAuthorFilters?filterType=${filterType}&itemBankId=${itemBankId}&workspaceRef=${workspaceRef}`;
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },

    async fetchCustomFieldsForRevisionData(contentRevisionId: number) {
        const url = `${apiRoots.CONTENT}GetCustomFieldsForRevision?contentRevisionId=${contentRevisionId}`;
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url)
        );
    },

    async fetchQuestionData(contentItemId: number, signal?: AbortSignal) {
        // Adding skipLogEvent true to prevent duplicate event logs and it will be handled in backed based on flags.
        if (!contentItemId || contentItemId === -1) {
            return undefined;
        }
        const url = `${apiRoots.ASSESSMENT}GetQuestion?id=${contentItemId}&testId=-1&verbose=on&languageAgnostic=true&skipLogEvent=true`;
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },

    async fetchGetQuestion(contentItemId: number, signal?: AbortSignal) {
        const url = `${apiRoots.TESTPLAYER}GetQuestion?contentItemId=${contentItemId}`;
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },
    async fetchGetSection(contentItemId: number, options?: { context?: string }, signal?: AbortSignal) {
        let url = `${apiRoots.TESTPLAYER}GetSection?contentItemId=${contentItemId}`;
        if (options?.context) {
            url += `&context=${options.context}`;
        }
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },

    async fetchGetSectionByRevisionId(contentRevisionId: number, signal?: AbortSignal) {
        const url = `${apiRoots.TESTPLAYER}GetSection?contentRevisionId=${contentRevisionId}`;
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },

    async fetchSettings(contentItemId: number, signal?: AbortSignal) {
        const url = `${apiRoots.TESTPLAYER}GetSettings?contentItemId=${contentItemId}`;
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },

    async setSettingValue(name: string, value: string, objectId: number, signal?: AbortSignal) {
        const url = `${apiRoots.TESTPLAYERADMIN}SetSettingValue?objectId=${objectId}&name=${name}&value=${value}`;
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, {}, signal)
        );
    },

    async editObjectCustomFieldMetadata(customFieldDefinitionId: number, contentItemIds: number[], values: any[], signal?: AbortSignal) {
        const url = `${apiRoots.METADATA}EditObjectCustomFieldMetadata`;
        const data = { contentItemIds, customFieldDefinitionId, values };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, data, signal)
        );
    },

    async editObjectCustomFieldsMetadata(
        contentItemIds: number[],
        customFieldUpdates: any[],
        contextContentItemId?: number,
        signal?: AbortSignal
    ) {
        const url = `${apiRoots.ASSESSMENT}EditObjectCustomFieldMetadataInContext`;
        const data: any = {
            contentItemIds,
            jsonStructure: JSON.stringify(customFieldUpdates),
        };

        if (contextContentItemId) {
            data.contextContentItemId = contextContentItemId;
        }

        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, data, signal)
        );
    },

    async EditQuestionInContext(
        textContent: string,
        contentItemId: number,
        contextContentItemId?: number,
        dakKeyEvent?: DynamicAnswerKeyEvents,
        signal?: AbortSignal
    ) {
        const url = `${apiRoots.ASSESSMENT}EditQuestionInContext`;
        let data: EditQuestionInContextReq = {
            textContent,
            contentItemId,
            contextContentItemId,
        };
        if (dakKeyEvent) {
            data = { ...data, event: dakKeyEvent };
        }
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, data, signal)
        );
    },

    async EditQuestionStandalone(
        textContent: string,
        contentItemId: number,
        signal?: AbortSignal
    ) {
        const url = `${apiRoots.ASSESSMENT}EditQuestionStandalone`;
        const data = {
            textContent,
            contentItemId,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, data, signal)
        );
    },

    async EditSectionLayout(
        payload: any,
        contextContentItemId: number,
        parentId: number,
        isTemplate: boolean,
        stimulusId? : number,
        signal?: AbortSignal,
    ) {
        const url = `${apiRoots.ASSESSMENT}EditSectionLayout`;
        const {
            layout, allowStimulusResizing, stimulusSize,
        } = payload;
        const data = {
            contextContentItemId,
            sectionId: parentId,
            layout,
            allowStimulusResizing,
            stimulusSize,
            isTemplate,
            ...(stimulusId && { stimulusId }),
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, data, signal)
        );
    },
    // eslint-disable-next-line max-params
    async CreateStimulusInContext(
        payload: any,
        contextContentItemId: number,
        parentId: number,
        language: string,
        isTemplate: boolean,
        newStimulusXML: string,
        stimulusContentRevisionId: number,
        signal?: AbortSignal
    ) {
        const {
            layout, allowStimulusResizing, stimulusSize,
        } = payload;
        const url = `${apiRoots.ASSESSMENT}CreateStimulusInContext`;
        const data = {
            contextContentItemId,
            textContent: newStimulusXML,
            position: 1,
            parentId,
            layout,
            allowStimulusResizing,
            stimulusSize,
            isTemplate,
            ...(stimulusContentRevisionId && { stimulusContentRevisionId }),
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, data, signal)
        );
    },

    async CreateQuestionInContext(
        payload: any,
        contextContentItemId: number,
        parentId: number,
        isTemplate: boolean,
        signal?: AbortSignal
    ) {
        const url = `${apiRoots.ASSESSMENT}CreateQuestionInContext`;
        const { position, textContent } = payload;
        const data = {
            contextContentItemId,
            position,
            textContent,
            parentId,
            isTemplate,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, data, signal)
        );
    },

    async CreateQuestionStandalone(
        payload: any,
        signal?: AbortSignal,
    ) {
        const url = `${apiRoots.ASSESSMENT}CreateQuestionStandalone`;
        const {
            languageAgnostic, questionType, textContent, itemBankId, isTemplate = false,
        } = payload;
        const data = {
            languageAgnostic,
            objectType: questionType,
            textContent,
            itemBankId,
            isTemplate,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, data, signal)
        );
    },

    async CreateContentInContextFromTemplate(payload: any, signal?: AbortSignal) {
        const url = `${apiRoots.ASSESSMENT}CreateContentInContextFromTemplate`;

        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, payload, signal)
        );
    },

    async setPublishStatus(publishStatus: string, contentItemId: number, signal?: AbortSignal) {
        const url = `${apiRoots.CONTENT}SetPublishStatus`;
        const data = { contentItemIds: [contentItemId], status: publishStatus };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, data, signal)
        );
    },

    async setDateableValue(contentItemId: number, itemProperty: string, itemValueId: number, signal?: AbortSignal) {
        const url = `${apiRoots.METADATA}SetItemValueOnContentItem`;
        const data = { contentItemIds: [contentItemId], itemProperty, itemValueId };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, data, signal)
        );
    },

    async getRevisionRelationStatus(contentRevisionId: number, answerKeyTypeRevisionId?: number, signal?: AbortSignal) {
        const url = `${apiRoots.CONTENT}GetRevisionRelationStatus?contentRevisionId=${contentRevisionId}&relatedObjectId=${answerKeyTypeRevisionId}`;
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },

    async getItemBankInfo(itemBankId: number, signal?: AbortSignal): Promise<ItemBankInfo> {
        const url = `${apiRoots.ASSESSMENT}GetItemBankInfo?itemBankId=${itemBankId}`;
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },

    async moveItemWithinContext(
        contextContentItemId: number,
        ids: number,
        position?: number,
        parentId?: number,
        signal?: AbortSignal
    ) {
        const url = `${apiRoots.ASSESSMENT}MoveWithinContext?`;
        const data = {
            contextContentItemId,
            ids,
            position,
            parentId,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, data, signal)
        );
    },

    async removeQuestionFromContext(
        contextContentItemId: number,
        contentItemIds: number,
        parentId?: number,
        position?: number,
        signal?: AbortSignal
    ) {
        const url = `${apiRoots.ASSESSMENT}RemoveFromContext?`;
        const data = {
            contextContentItemId,
            contentItemIds,
            position,
            parentId,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, data, signal)
        );
    },

    async setQuestionRefWeight(
        contextContentItemId: number,
        contentItemId: number,
        weight: number,
        signal?: AbortSignal
    ) {
        const url = `${apiRoots.ASSESSMENT}SetQuestionRefWeight?`;
        const data = {
            contextContentItemId,
            contentItemId,
            weight,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, data, signal)
        );
    },

    async saveItemAsTemplate(
        contentItemId: number,
        title: string,
        publishStatus = 'draft',
        signal?: AbortSignal
    ) {
        const url = `${apiRoots.ASSESSMENT}SaveContentAsTemplate?`;
        const data = {
            contentItemId, publishStatus, title,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, data, signal)
        );
    },

    async copyContentItem(
        contentItemId: number,
        itemBankId: number,
        copyTitle: string,
        signal?: AbortSignal
    ) {
        const url = `${apiRoots.CONTENT}CopyContentItem?`;
        const data = {
            contentItemId,
            withSubItems: true,
            returnNewItems: true,
            copyTitle,
            itemBankId,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, data, signal)
        );
    },

    async removeInlineSectionFromContext(
        contextContentItemId: number,
        sectionId: number,
        signal?: AbortSignal
    ) {
        const url = `${apiRoots.ASSESSMENT}DeleteInlineSection?`;
        const data = {
            contextContentItemId,
            sectionId,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, data, signal)
        );
    },

    async addContentToContext(
        contextContentItemId: number,
        contentRevisionIds: string,
        parentId?: number,
        position?: number,
        signal?: AbortSignal
    ) {
        const url = `${apiRoots.ASSESSMENT}AddContentToContext?`;
        const data = {
            contextContentItemId,
            contentRevisionIds,
            position,
            parentId,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, data, signal)
        );
    },

    async saveAuthorFilterTemplate(title: string, filterParams: string, filterType: string) {
        let url = `${apiRoots.ASSESSMENT}CreateFilter?filterType=${filterType}`;
        url = `${url}&title=${title}&parameters=${filterParams}`;
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url)
        );
    },

    async updateAuthorFilterTemplate(filterId: number, title: string, tabParams: string) {
        let url = `${apiRoots.ASSESSMENT}UpdateFilter?filterId=${filterId}`;
        url = `${url}&title=${title}&parameters=${tabParams}`;
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url)
        );
    },

    async addInlineSectionToContext(
        position: number,
        contextContentItemId: number,
        title: string,
        signal?: AbortSignal
    ) {
        const url = `${apiRoots.ASSESSMENT}CreateInlineSection?`;
        const data = {
            position,
            contextContentItemId,
            title,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, data, signal)
        );
    },

    async getAssetUploadTicket(itemBankId: number, fileName: string, fileSize: number) {
        const url = `${apiRoots.ASSESSMENT}GetAssetUploadTicket`;
        const postData: object = {
            itemBankId,
            fileName,
            fileSize,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },

    async assetUploadSuccess(itemBankId: number, ticket: string, title: string) {
        const url = `${apiRoots.ASSESSMENT}AssetUploadSuccess`;
        const postData: object = {
            itemBankId,
            ticket,
            title,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },

    async attachResourceToContext(contextContentItemId: number, contentItemId: number, title: string) {
        const url = `${apiRoots.ASSESSMENT}AttachResourceToContext`;
        const postData: object = {
            contextContentItemId,
            contentItemId,
            title,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },

    async logAssetViewed(
        contextContentItemId: number,
        contentItemId: number,
        title?: string,
        link?: string,
        signal?: AbortSignal
    ) {
        const url = `${apiRoots.ASSESSMENT}LogAssetViewed`;
        const postData: object = {
            contextContentItemId,
            contentItemId,
            title,
            link,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData, signal)
        );
    },

    async editResourceInContext(
        contextContentItemId: number,
        resourceContentItemId: number,
        textContent: string,
        isTemplate?: boolean,
    ) {
        const url = `${apiRoots.ASSESSMENT}EditResourceInContext`;
        const postData: object = {
            contextContentItemId,
            resourceContentItemId,
            textContent,
            isTemplate,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },

    async addOrUpdateUrlToContext(
        contextContentItemId: number,
        title: string,
        link: string,
        rootUrl?: string,
        linkMatch = 'match_all_after',
    ) {
        const url = `${apiRoots.ASSESSMENT}AddOrUpdateURLToContext`;
        const postData: object = {
            contextContentItemId,
            title,
            link,
            linkMatch,
            rootUrl,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },

    async removeResourceFromContext(
        contextContentItemId: number,
        contentItemId: number,
        link: string,
        signal?: AbortSignal
    ) {
        const url = `${apiRoots.ASSESSMENT}RemoveResourceFromContext?`;
        const data = {
            contextContentItemId,
            contentItemId, // valid contentItemId takes precedence over link
            link,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, data, signal)
        );
    },

    async removeURLFromContext(
        contextContentItemId: number,
        link: string,
        signal?: AbortSignal
    ) {
        const url = `${apiRoots.ASSESSMENT}RemoveURLFromContext?`;
        const data = {
            contextContentItemId,
            link,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, data, signal)
        );
    },

    async renameContextItem(
        contextContentItemId: number,
        title: string,
        signal?: AbortSignal
    ) {
        const url = `${apiRoots.ASSESSMENT}RenameContextItem?`;
        const data = {
            contextContentItemId,
            title,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, data, signal)
        );
    },

    async renameInlineSection(
        title: string,
        sectionId: number,
        contextContentItemId: number,
        signal?: AbortSignal
    ) {
        const url = `${apiRoots.ASSESSMENT}RenameInlineSection?`;
        const data = {
            contextContentItemId,
            sectionId,
            title,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, data, signal)
        );
    },

    async renameContentInContext(
        contextContentItemId: number,
        contentItemId: number,
        title: string,
        signal?: AbortSignal
    ) {
        const url = `${apiRoots.ASSESSMENT}RenameContentInContext?`;
        const data = {
            contextContentItemId,
            contentItemId,
            title,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, data, signal)
        );
    },

    async renameResourceInContext(
        contextContentItemId: number,
        resourceContentItemId: number,
        title: string,
        signal?: AbortSignal
    ) {
        const url = `${apiRoots.ASSESSMENT}RenameResourceInContext?`;
        const data = {
            contextContentItemId,
            resourceContentItemId,
            title,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, data, signal)
        );
    },

    async fetchContentEditStatus(contentRevisionId: number, contextId?: number, signal?: AbortSignal) {
        if (contentRevisionId == null) {
            return { success: false };
        }

        let url = `${apiRoots.ASSESSMENT}GetContentEditStatus?contentRevisionId=${contentRevisionId}`;

        if (contextId) {
            url = `${url}&contextId=${contextId}`;
        }
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },

    async fetchContentPresetItems(contentItemId: number, contentType: string, presetType: string, itemBankId: number, signal?: AbortSignal) {
        const url = `${apiRoots.ASSESSMENT}GetContentPresets?`;
        const data = {
            contentItemId, contentType, presetType, itemBankId,
        };
        return requestService.executeRequest(
            requestService.createFetchUrlWithQueryParams(url, data, signal)
        );
    },

    async fetchContentPreset(contentItemId: number, contentRevisionId = -1, signal?: AbortSignal) {
        let url = `${apiRoots.ASSESSMENT}GetContentPreset?contentItemId=${contentItemId}`;
        if (contentRevisionId > 0) {
            url = `${url}&revisionId=${contentRevisionId}`;
        }
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },

    async fetchListDataDeliverySource(
        itemBankId: number,
        contentRevisionId: number,
        datasourceName: string,
        payloadJSON: any,
        useMeasureIdForRevision?: boolean,
        signal?: AbortSignal,
    ): Promise<DataSourceResponse> {
        const url = `${apiRoots.ASSESSMENT}ListDataDiscoverySource`;
        const data = {
            itemBankId,
            contentRevisionId,
            datasourceName,
            payloadJSON,
            hitsPerPage: 10000, // probably will cover all items and we will not get paggination at all
            useMeasureIdForRevision,
        };

        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, data, signal)
        );
    },

    async fetchIRTListDataDeliverySource(
        itemBankId: number,
        contentRevisionId: number,
        payloadJSON: any,
        datasourceName: string,
        majorVersionId?: number,
        majorVersionCreatedDate?: string,
        signal?: AbortSignal,
    ): Promise<DataSourceResponse> {
        const url = `${apiRoots.ASSESSMENT}ListDataDiscoverySource`;
        const data = {
            itemBankId,
            contentRevisionId,
            datasourceName,
            majorVersionId,
            majorVersionCreatedDate,
            payloadJSON,
        };

        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, data, signal)
        );
    },

    async fetchCurrentContentPreset(contentRevisionId: number, signal?: AbortSignal) {
        const url = `${apiRoots.ASSESSMENT}GetContentPreset?revisionId=${contentRevisionId}`;
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },

    async updateContentInContextToLiveRevision(
        contextContentItemId: number,
        contentItemId: number,
        signal?: AbortSignal
    ) {
        const url = `${apiRoots.ASSESSMENT}UpdateContentInContextToLiveRevision?`;
        const data: UpdateContentInContextToLiveRevisionData = {
            contextContentItemId,
            contentItemId,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, data, signal)
        );
    },

    async updateTemplateAndContentInContext(
        contextContentItemId: number,
        contentItemId: number,
        checkedOptions: UpdateCheckStatus,
        templateContentItemId: number,
        signal?: AbortSignal
    ) {
        const url = `${apiRoots.ASSESSMENT}UpdateTemplateAndContentInContext?`;
        const data: any = {
            contextContentItemId,
            contentItemId,
        };
        if (templateContentItemId) {
            data.templateContentItemId = templateContentItemId;
        }
        if (checkedOptions) {
            const { updateTemplateChecked, updateQuestionChecked } = checkedOptions;
            data.updateTemplateToLatestRevision = !!updateTemplateChecked;
            data.updateContentToLatestRevision = !!updateQuestionChecked;
            // When updateTemplate is checked we need to strictly check for updateQuestionChecked to be false condition,
            // else null/undefined can be treatead as falsy value when updateTemplateChecked is falsy
            if ((updateTemplateChecked && updateQuestionChecked === false) || (!updateTemplateChecked && !updateQuestionChecked)) {
                data.duplicateAndReplaceContentInContext = true;
            }
        }
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, data, signal)
        );
    },

    async duplicateAndReplaceContentInContext(
        contextContentItemId: number,
        contentItemId: number,
        signal?: AbortSignal
    ) {
        const url = `${apiRoots.ASSESSMENT}DuplicateAndReplaceContentInContext?`;
        const data = {
            contextContentItemId,
            contentItemId,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, data, signal)
        );
    },

    async lockItem(contentItemId: number, signal?: AbortSignal) {
        const url = `${apiRoots.ASSESSMENT}LockItem?`;
        const data = {
            contentItemId,
            lockItem: true,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, data, signal)
        );
    },

    async unlockItem(contentItemId: number, signal?: AbortSignal) {
        const url = `${apiRoots.ASSESSMENT}LockItem?`;
        const data = {
            contentItemId,
            lockItem: false,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, data, signal)
        );
    },

    async generateAnswerKeyOutput(answerKeyTypeRevisionId: number, answerKeyInputJSON: string, signal?: AbortSignal) {
        const url = `${apiRoots.ASSESSMENT}GenerateAnswerKeyOutput?`;
        const data = {
            answerKeyTypeRevisionId,
            answerKeyInputJSON,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, data, signal)
        );
    },

    async generateDummyContentForPreview(textContent: string, contextId: number, signal?: AbortSignal) {
        const url = `${apiRoots.ASSESSMENT}GenerateDummyContentForPreview?`;
        const data = {
            textContent,
            contextId,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, data, signal)
        );
    },

    async getQuestionSetSummary(assessmentId: number, revisionId?: number, signal?: AbortSignal) {
        const revisionIdValue = typeof revisionId === 'number' && revisionId > 0 ? revisionId : null;
        const url = `${apiRoots.ASSESSMENT}GetQuestionSetSummary?assessmentId=${assessmentId}&revisionId=${revisionIdValue}&context=author`;
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },

    async getQuestionTestCases(contentItemId: number, signal?: AbortSignal) {
        const url = `${apiRoots.ASSESSMENT}GetQuestionTestCases?contentItemId=${contentItemId}`;
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },

    async editQuestionTestCases(contentItemId: number, textContent: string, signal?: AbortSignal) {
        const url = `${apiRoots.ASSESSMENT}EditQuestionTestCases?`;
        const data = {
            contentItemId,
            textContent,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, data, signal)
        );
    },

    async getSubmitTask(action: string, contentRevisionId: number, textContent?: string, signal?: AbortSignal) {
        const url = `${apiRoots.ASYNC}SubmitTask?`;
        const parameters = {
            contentRevisionId,
            textContent,
        };
        const data: object = {
            repository: 'assessment',
            action,
            parametersJSON: JSON.stringify(parameters),
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, data, signal)
        );
    },

    async checkStatus(jobId: string, signal?: AbortSignal) {
        const url = `${apiRoots.ASYNC}CheckStatus`;
        const postData: object = {
            jobId,
        };

        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(
                url,
                postData,
                signal
            )
        );
    },

    async restoreItem(contentItemId: number, visibility: string, signal?: AbortSignal) {
        const url = `${apiRoots.ASSESSMENT}SetContentItemVisibility`;
        const parameters = {
            contentItemId,
            visibility,
        };

        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, parameters, signal)
        );
    },

    async fetchCustomUserRoles() {
        const url = `${apiRoots.ASSESSMENT}GetCustomUserRoles?uiToolFilters=author`;

        return requestService.executeRequest(
            requestService.createFetchRequestObject(url)
        );
    },

    async fetchAllCustomUserRoles() {
        const url = `${apiRoots.ASSESSMENT}GetCustomUserRoles?listAllRoles=true`;

        return requestService.executeRequest(
            requestService.createFetchRequestObject(url)
        );
    },

    async addApplicationsToBlocklist(payloadData: AddApplicationsPayload & { marketplaceId: number }) {
        const url = `${apiRoots.ASSESSMENT}UpdateSoftwareBlocklist`;

        const baseParams: Record<string, any> = {
            targetMarketplaceName: window.marketplaceName,
            targetMarketplaceId: payloadData.marketplaceId,
            os: payloadData.osData,
            config: payloadData.configNameData,
        };

        const formatSoftwares = (apps?: string) => apps?.split(',').map((item) => item.trim()).join(',') || '';

        let params: Record<string, any>;

        if (payloadData?.hashSoftwareAppData) {
            params = {
                ...baseParams,
                softwares: formatSoftwares(payloadData?.hashSoftwareAppData),
                input: payloadData?.hashValuesAppsData,
                operation: ADD_SOFTWARE_WITH_HASHES,
            };
        } else {
            params = {
                ...baseParams,
                softwares: formatSoftwares(payloadData.softwareAppsData),
                operation: ADD_SOFTWARE,
            };
        }

        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, params)
        );
    },

    async addHashesToApplication(payloadData: AddHashesPayload & { marketplaceId: number }) {
        const url = `${apiRoots.ASSESSMENT}UpdateSoftwareBlocklist`;
        const params: Record<string, any> = {
            targetMarketplaceName: window.marketplaceName,
            targetMarketplaceId: payloadData.marketplaceId,
            operation: ADD_HASHES,
            softwares: payloadData.initialData.text,
            input: Array.isArray(payloadData.input) ? payloadData.input.map((item) => item.trim()).join(',') : payloadData.input?.trim(),
            os: payloadData.osData,
        };

        if (payloadData?.configNameData) {
            params.config = payloadData.configNameData;
        }

        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, params)
        );
    },

    async fetchBlocklistConfigByName(
        payloadData: ConfigInfo,
        isSortByDateDesc: boolean,
        searchText?: string,
        viewHashes?: boolean,
        processName?: string
    ) {
        let url = `${apiRoots.ASSESSMENT}ViewSoftwareBlocklist?`
            + `configName=${payloadData.configNameData}`
            + `&os=${payloadData.osData}`
            + `&sort=${isSortByDateDesc ? 'desc' : 'asc'}`;

        if (searchText) {
            url += `&processName=${searchText}`;
        }

        if (viewHashes) {
            url += '&viewHashes=true';
        }

        if (processName) {
            url += `&processName=${processName}`;
        }

        return requestService.executeRequest(
            requestService.createFetchRequestObject(url)
        );
    },

    async deleteApplications(
        applicationsToDelete: string[],
        marketplaceId: number,
        osData: 'MacOS' | 'Windows',
        configName: string,
    ) {
        const url = `${apiRoots.ASSESSMENT}UpdateSoftwareBlocklist`;
        const params: Record<string, any> = {
            targetMarketplaceName: window.marketplaceName,
            targetMarketplaceId: marketplaceId,
            os: osData,
            config: configName,
            operation: REMOVE_SOFTWARE,
            softwares: applicationsToDelete.map((item) => item.trim()).join(','),
        };

        if (configName) {
            params.config = configName;
        }

        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, params)
        );
    },

    async deleteHashes(
        hashesToDelete: string[],
        marketplaceId: number,
        osData: 'MacOS' | 'Windows',
        configName: string,
        initialData: any,
    ) {
        const url = `${apiRoots.ASSESSMENT}UpdateSoftwareBlocklist`;
        const params: Record<string, any> = {
            targetMarketplaceName: window.marketplaceName,
            targetMarketplaceId: marketplaceId,
            os: osData,
            operation: REMOVE_HASHES,
            softwares: initialData,
            input: hashesToDelete.map((item) => item.trim()).join(','),
        };

        if (configName) {
            params.config = configName;
        }

        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, params)
        );
    },

    async deleteDeviceIds(deviceIdsToRemove: string[], marketplaceId: number) {
        const url = `${apiRoots.ASSESSMENT}UpdateDeviceIdBlocklist`;
        const params = {
            targetMarketplaceName: window.marketplaceName,
            targetMarketplaceId: marketplaceId,
            toRemove: deviceIdsToRemove.map((item) => item.trim()).join(','),
        };

        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, params)
        );
    },

    async fetchDeviceIds(isSortByDateDesc: boolean, searchText?: string) {
        let url = `${apiRoots.ASSESSMENT}ViewDeviceIdBlocklist?sort=${isSortByDateDesc ? 'desc' : 'asc'}`;

        if (searchText) {
            url += `&deviceId=${searchText}`;
        }

        return requestService.executeRequest(
            requestService.createFetchRequestObject(url)
        );
    },

    async addDeviceIdsToBlocklist(payloadData: AddDeviceIdsPayload & { marketplaceId: number }) {
        const url = `${apiRoots.ASSESSMENT}UpdateDeviceIdBlocklist`;
        const idsToAdd = payloadData.deviceIdsData.split(',').map((id) => id.trim()).join(',');
        const params = {
            targetMarketplaceName: window.marketplaceName,
            targetMarketplaceId: payloadData.marketplaceId,
            toAdd: idsToAdd,
        };

        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, params)
        );
    },

    async getAssessmentData(assessmentId: string) {
        const url = `${apiRoots.ASSESSMENT}GetAssessment?assessmentId=${assessmentId}&revisionId=null&context=author&_=${Date.now()}`;
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url)
        );
    },

    async updateAssessmentData(assessmentId: string, data: any) {
        const url = `${apiRoots.ASSESSMENT}EditAssessment?assessmentId=${assessmentId}&revisionId=null`;

        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, data)
        );
    },

    async addContentBlockSelectionData(assessmentId: string, data: any) {
        const url = `${apiRoots.ASSESSMENT}AddSectionSelectionForQuestionSet?assessmentId=${assessmentId}&revisionId=null`;
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, data)
        );
    },

    async getExitLockdownPassword() {
        const url = `${apiRoots.ASSESSMENT}GetOrCreateIIBEarlyLockdownExitPassword`;
        return requestService.executeRequest(
            requestService.createFetchRequestForExitLockdownPassword(url)
        );
    },

    async createContentFromTemplate(
        payload: {
                title: string;
                name: string;
                itemBankId: number;
                templateContentRevisionId: number;
            },
        signal?: AbortSignal
    ) {
        const url = `${apiRoots.ASSESSMENT}CreateContentFromTemplate`;
        return requestService.executeRequest(requestService.createFormDataPostRequestObject(url, payload, signal));
    },

    async fetchAssessmentDetails(contentItemId: string) {
        const url = `${apiRoots.ASSESSMENT}GetAssessmentDetails?contentItemId=${contentItemId}`;
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url)
        );
    },

    async endImpersonation() {
        const url = `${apiRoots.ADMIN}EndImpersonation`;
        return requestService.executeRequest(
            requestService.createJsonPostRequestWithTokenObject(url, {})
        );
    },

    async addPciResourceToContext(payload: {
        contextContentItemId: number;
        resourceContentItemId: number;
        resourceContentRevisionId?: number;
        title: string;
    }) {
        const url = `${apiRoots.ASSESSMENT}AddPCIResourceToContext`;
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, payload)
        );
    },

    async fetchNewFilterDataFromSource(
        sourcePath: string,
        param?: string,
        signal?: AbortSignal
    ) {
        const url: string = `${apiRoots.METADATA}${sourcePath}?${param ?? "itemProperty=assessment_filter_tags&term="}`;
        
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },
};

export function* safe(
    featureId: string,
    effect: any,
    actionKey = 'generic',
    errorHandler?: ApiErrorHandler
): Generator<any, any, any> {
    const errorHandlerInternal = errorHandler || apiErrorHandler;
    try {
        const response = yield effect;
        if (response.success || response?.alert?.key) {
            return response;
        }
        if (!response?._alertHandled) {
            yield errorHandlerInternal(featureId, response, actionKey);
        }
        return response;
    } catch (error) {
        if (!error?._alertHandled) {
            yield errorHandlerInternal(featureId, { error }, actionKey);
        }
        return { error };
    }
}

export function* safeArrayResponse(
    featureId: string,
    effect: any,
    actionKey = 'generic',
    errorHandler?: ApiErrorHandler
): Generator<any, any, any> {
    const errorHandlerInternal = errorHandler || apiErrorHandler;
    try {
        const response = yield effect;
        if (Array.isArray(response)) {
            return response;
        }
    } catch (error) {
        yield errorHandlerInternal(featureId, { error }, actionKey);
    }
    return null;
}

function* apiErrorHandler(featureId: string, response: any, actionKey = 'generic') {
    const { message, error } = response;
    const alert: Alert = {
        key: `${actionKey}.error.title`,
        info: `${actionKey}.error.description`,
        level: AlertLevel.ERROR,
        payload: {},
    };
    if (error) {
        console.error(`${actionKey} failed`, error);
    } else if (message) {
        console.error(`${actionKey} failed with message`, message);
    }

    yield put(pushAlert(alert));
}

export default apiService;
